function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Check } from '@styled-icons/material/Check';
import { Text, TextVariant } from '@naf/text';
import { spacing, fontStyle, themeLight } from '@naf/theme';
export let LabelVariant = /*#__PURE__*/function (LabelVariant) {
  LabelVariant["OnOff"] = "OnOff";
  LabelVariant["YesNo"] = "YesNo";
  LabelVariant["None"] = "None";
  return LabelVariant;
}({});
export const Toggle = /*#__PURE__*/forwardRef(({
  name,
  disabled = false,
  checked = false,
  onChange,
  className,
  labelOption,
  ...rest
}, ref) => {
  const [isChecked, setIsChecked] = useState(checked);
  const handleChange = e => {
    setIsChecked(!isChecked);
    if (onChange) {
      onChange({
        name: name,
        value: e.target.checked
      });
    }
  };
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);
  const getLabels = () => {
    switch (labelOption) {
      case LabelVariant.OnOff:
        return {
          off: 'Av',
          on: 'På'
        };
      case LabelVariant.YesNo:
        return {
          off: 'Nei',
          on: 'Ja'
        };
      case LabelVariant.None:
        return {
          off: '',
          on: ''
        };
      default:
        return {
          off: '',
          on: ''
        };
    }
  };
  const labels = getLabels();
  return /*#__PURE__*/React.createElement(ToggleWrapper, null, labelOption && /*#__PURE__*/React.createElement(LabelText, {
    variant: TextVariant.Small,
    color: themeLight.typography.subtleText
  }, labels.off), /*#__PURE__*/React.createElement(ToggleContainer, {
    $disabled: disabled,
    className: className,
    $labelOption: labelOption
  }, /*#__PURE__*/React.createElement(ToggleSwitch, _extends({
    type: "checkbox",
    name: name,
    id: name,
    disabled: disabled,
    checked: isChecked,
    onChange: handleChange,
    tabIndex: 0,
    ref: ref
  }, rest)), /*#__PURE__*/React.createElement(Label, {
    htmlFor: name
  }, /*#__PURE__*/React.createElement(Holder, null), /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(StyledCheck, {
    $isActive: isChecked,
    $isDisabled: disabled,
    size: 16
  })))), labelOption && /*#__PURE__*/React.createElement(LabelText, {
    variant: TextVariant.Small,
    color: themeLight.typography.subtleText
  }, labels.on));
});
export default Toggle;
const ToggleWrapper = styled.div.withConfig({
  displayName: "toggle__ToggleWrapper",
  componentId: "sc-1f7e37o-0"
})(["display:inline-flex;align-items:center;"]);
const LabelText = styled(Text).withConfig({
  displayName: "toggle__LabelText",
  componentId: "sc-1f7e37o-1"
})(["line-height:0;"]);
const ToggleContainer = styled.div.withConfig({
  displayName: "toggle__ToggleContainer",
  componentId: "sc-1f7e37o-2"
})(["display:inline-flex;position:relative;width:", ";", " ", ""], spacing.space48, ({
  $labelOption
}) => $labelOption && css(["margin:0 ", ";"], spacing.space8), ({
  $disabled
}) => $disabled && css(["", "{cursor:not-allowed;}", "{&:before,&:after{color:", ";background-color:", ";}}", "{background-color:", ";}"], Label, Holder, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled, ({
  theme
}) => theme.componentColors ? theme.componentColors.navigation.inactive : themeLight.componentColors.navigation.inactive, Switch, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled));
const Label = styled.label.withConfig({
  displayName: "toggle__Label",
  componentId: "sc-1f7e37o-3"
})(["display:flex;overflow:hidden;cursor:pointer;border-radius:calc(", " / 2);margin:0;"], spacing.space24);
const Holder = styled.span.withConfig({
  displayName: "toggle__Holder",
  componentId: "sc-1f7e37o-4"
})(["display:flex;align-items:center;width:100%;margin-left:-100%;transition:margin-left 0.3s ease-in;", ";&:before,&:after{display:flex;width:100%;height:", ";padding:0;box-sizing:border-box;color:", ";}&:before{content:'';padding-left:", ";background-color:", ";}&:after{content:'';padding-right:", ";background-color:", ";}svg{visibility:hidden;position:absolute;background-color:transparent;margin-left:4px;transition:visibility 0s 0s;}"], fontStyle.bodyText.bodyText, spacing.space24, ({
  theme
}) => theme.componentColors ? theme.componentColors.inputElement.borderActive : themeLight.componentColors.inputElement.borderActive, spacing.space48, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, spacing.space48, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.disabled : themeLight.componentColors.interactiveElement.disabled);
const Switch = styled.span.withConfig({
  displayName: "toggle__Switch",
  componentId: "sc-1f7e37o-5"
})(["display:flex;width:", ";margin:", ";background-color:", ";position:absolute;top:0;bottom:0;right:", ";border-radius:50%;transition:all 0.3s ease-in;"], spacing.space16, spacing.space4, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space24);
const StyledCheck = styled(Check).withConfig({
  displayName: "toggle__StyledCheck",
  componentId: "sc-1f7e37o-6"
})(["color:", ";"], ({
  $isActive,
  $isDisabled,
  theme
}) => $isDisabled ? theme.componentColors.interactiveElement.disabled : $isActive ? theme.componentColors.interactiveElement.active : 'hidden');
const ToggleSwitch = styled.input.withConfig({
  displayName: "toggle__ToggleSwitch",
  componentId: "sc-1f7e37o-7"
})(["display:none;&:checked + ", "{", "{margin-left:0;svg{visibility:visible;transition:visibility 0s 0.3s;}}", "{right:0;background-color:", ";}}"], Label, Holder, Switch, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText);
//# sourceMappingURL=toggle.js.map